import { AccountType, CoreUser } from '../types';
import { passportRolesFromClaims, resolveActiveRole, rolesFromClaims } from '../utils/userDisplay';

const TOKEN_KEY = 'wandawazi_access_token';
const EXPIRES_AT_KEY = 'wandawazi_token_expires_at';
const REFRESH_TOKEN_KEY = 'wandawazi_refresh_token';
const USER_KEY = 'wandawazi_user';
// Kept across logout so the next sign-in can reuse each user's role choice.
const ACTIVE_ROLE_KEY = 'wandawazi_active_roles';

// Bumps whenever the access token is replaced, so an in-flight refresh can
// tell that a newer sign-in or organisation switch already won.
let sessionGeneration = 0;

function isAccountType(value: unknown): value is AccountType {
  return value === 'employee' || value === 'manager' || value === 'verifier';
}

function readRolePreferences(): Record<string, AccountType> {
  const raw = localStorage.getItem(ACTIVE_ROLE_KEY);
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    const preferences: Record<string, AccountType> = {};
    for (const [userId, role] of Object.entries(parsed)) {
      if (isAccountType(role)) preferences[userId] = role;
    }
    return preferences;
  } catch {
    return {};
  }
}

export interface StoredSession {
  accessToken: string;
  expiresIn: number;
  refreshToken?: string;
  user: CoreUser;
}

export function decodeTokenClaims(token: string): Record<string, unknown> {
  try {
    const payload = token.split('.')[1];
    if (!payload) return {};
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
    return JSON.parse(atob(padded)) as Record<string, unknown>;
  } catch {
    return {};
  }
}

export const authStorage = {
  getToken(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  },

  getRefreshToken(): string | null {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  },

  getAccessTokenExpiresAt(): number {
    return Number(localStorage.getItem(EXPIRES_AT_KEY) ?? '0');
  },

  getCachedUser(): CoreUser | null {
    const raw = localStorage.getItem(USER_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as CoreUser;
    } catch {
      return null;
    }
  },

  /** Generation of the session currently in storage. */
  currentGeneration(): number {
    return sessionGeneration;
  },

  setSession({ accessToken, expiresIn, refreshToken, user }: StoredSession): void {
    sessionGeneration += 1;
    localStorage.setItem(TOKEN_KEY, accessToken);
    if (refreshToken) {
      localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    }
    if (expiresIn > 0) {
      localStorage.setItem(EXPIRES_AT_KEY, String(Date.now() + expiresIn * 1000));
    }
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  },

  updateTokens(accessToken: string, expiresIn: number, refreshToken: string): void {
    sessionGeneration += 1;
    localStorage.setItem(TOKEN_KEY, accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    localStorage.setItem(EXPIRES_AT_KEY, String(Date.now() + expiresIn * 1000));
  },

  updateCachedUser(user: CoreUser): void {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  },

  getTokenClaims(): Record<string, unknown> {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return {};
    return decodeTokenClaims(token);
  },

  /** Saved role for the signed-in user, even if the current token no longer includes it. */
  getPreferredRole(): AccountType | null {
    const subject = this.getTokenClaims().sub;
    if (typeof subject !== 'string' || !subject) return null;
    return readRolePreferences()[subject] ?? null;
  },

  /** Role to act as: the saved choice when Core still assigned it, otherwise the highest role. */
  getActiveRole(): AccountType | null {
    return resolveActiveRole(rolesFromClaims(this.getTokenClaims()), this.getPreferredRole());
  },

  setActiveRole(role: AccountType): void {
    const claims = this.getTokenClaims();
    const subject = claims.sub;
    if (typeof subject !== 'string' || !subject) return;
    if (!passportRolesFromClaims(rolesFromClaims(claims)).includes(role)) return;
    const preferences = readRolePreferences();
    preferences[subject] = role;
    localStorage.setItem(ACTIVE_ROLE_KEY, JSON.stringify(preferences));
  },

  clearSession(): void {
    sessionGeneration += 1;
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(EXPIRES_AT_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  },
};
